/**
 * Admin Panel — Users, Stats, Subscription management
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminService } from '../../services/admin.service'
import type { AdminUser, AdminStats } from '../../services/admin.service'
import PromptsSection from '../../components/admin/PromptsSection'
import toast from 'react-hot-toast'

type AdminTab = 'users' | 'prompts'

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<AdminTab>('users')
  const { data: stats, isLoading: loadingStats } = useQuery({
    queryKey: ['adminStats'],
    queryFn: () => adminService.getStats(),
  })

  const tabs: { key: AdminTab; label: string }[] = [
    { key: 'users', label: 'Пользователи' },
    { key: 'prompts', label: 'AI Промпты' },
  ]

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>

      {/* Stats cards */}
      {loadingStats ? (
        <div className="animate-pulse grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-lg" />
          ))}
        </div>
      ) : stats ? (
        <StatsGrid stats={stats} />
      ) : null}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 text-sm font-medium border-b-2 transition ${
                activeTab === tab.key
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'users' && <UsersSection />}
      {activeTab === 'prompts' && <PromptsSection />}
    </div>
  )
}

// ============================================================
// STATS GRID
// ============================================================

function StatsGrid({ stats }: { stats: AdminStats }) {
  const cards = [
    { label: 'Users', value: stats.total_users, sub: `${stats.users_registered_7d} this week` },
    { label: 'Verified', value: stats.verified_users, sub: `${stats.total_users - stats.verified_users} unverified` },
    { label: 'Active Subs', value: stats.active_subscriptions, sub: `${stats.expired_subscriptions} expired` },
    { label: 'TG Connected', value: stats.telegram_connected },
    { label: 'Channels', value: stats.total_channels },
    { label: 'Leads', value: stats.total_leads },
    { label: 'AI Tokens Today', value: stats.ai_tokens_today.toLocaleString(), sub: `$${stats.ai_cost_today.toFixed(3)}` },
    { label: 'AI Tokens Total', value: stats.ai_tokens_total.toLocaleString(), sub: `$${stats.ai_cost_total.toFixed(2)}` },
  ]

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map((c) => (
        <div key={c.label} className="card py-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">{c.label}</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{c.value}</div>
          {c.sub && <div className="text-xs text-gray-400 mt-0.5">{c.sub}</div>}
        </div>
      ))}
    </div>
  )
}

// ============================================================
// USERS SECTION
// ============================================================

function UsersSection() {
  const [search, setSearch] = useState('')
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null)

  const { data: users, isLoading } = useQuery({
    queryKey: ['adminUsers', search],
    queryFn: () => adminService.getUsers({ q: search || undefined, limit: 100 }),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Users</h2>
        <input
          type="text"
          placeholder="Search by email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field w-64"
        />
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded" />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                <th className="pb-2 pr-4">ID</th>
                <th className="pb-2 pr-4">Email</th>
                <th className="pb-2 pr-4">Plan</th>
                <th className="pb-2 pr-4">Expires</th>
                <th className="pb-2 pr-4">Tokens</th>
                <th className="pb-2 pr-4">AI Usage</th>
                <th className="pb-2 pr-4">Channels</th>
                <th className="pb-2 pr-4">TG</th>
                <th className="pb-2 pr-4">Role</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {users?.map((u) => (
                <UserRow key={u.id} user={u} onEdit={() => setEditingUser(u)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingUser && (
        <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} />
      )}
    </div>
  )
}

function UserRow({ user, onEdit }: { user: AdminUser; onEdit: () => void }) {
  const expired = user.subscription_expires_at
    ? new Date(user.subscription_expires_at) < new Date()
    : true

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="py-2 pr-4 text-gray-400">{user.id}</td>
      <td className="py-2 pr-4">
        <div className="flex items-center gap-1">
          <span className="font-medium text-gray-900">{user.email}</span>
          {user.email_verified && <span className="text-green-500 text-xs">~</span>}
          {!user.email_verified && <span className="text-red-400 text-xs">!</span>}
        </div>
      </td>
      <td className="py-2 pr-4">
        <span className={`px-2 py-0.5 rounded text-xs ${
          user.plan === 'scale' ? 'bg-purple-100 text-purple-700'
          : user.plan === 'startup' ? 'bg-blue-100 text-blue-700'
          : 'bg-gray-100 text-gray-600'
        }`}>
          {user.plan || 'none'}
        </span>
      </td>
      <td className="py-2 pr-4 text-xs">
        {user.subscription_expires_at ? (
          <span className={expired ? 'text-red-500' : 'text-green-600'}>
            {new Date(user.subscription_expires_at).toLocaleDateString()}
          </span>
        ) : '—'}
      </td>
      <td className="py-2 pr-4 text-xs text-gray-600">
        {user.tokens_used ?? 0}/{user.tokens_limit ?? 0}
      </td>
      <td className="py-2 pr-4 text-xs text-gray-600">
        {user.ai_tokens_total.toLocaleString()} (${user.ai_cost_usd.toFixed(2)})
      </td>
      <td className="py-2 pr-4 text-center">{user.channels_count}</td>
      <td className="py-2 pr-4">
        {user.telegram_connected ? (
          <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
        ) : (
          <span className="w-2 h-2 rounded-full bg-gray-300 inline-block" />
        )}
      </td>
      <td className="py-2 pr-4 text-xs">{user.role}</td>
      <td className="py-2">
        <button
          onClick={onEdit}
          className="text-xs text-primary-600 hover:text-primary-700 font-medium"
        >
          Edit
        </button>
      </td>
    </tr>
  )
}

// ============================================================
// EDIT USER MODAL
// ============================================================

function EditUserModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [plan, setPlan] = useState(user.plan || 'trial')
  const [subStatus, setSubStatus] = useState(user.subscription_status || 'active')
  const [expiresAt, setExpiresAt] = useState(
    user.subscription_expires_at
      ? user.subscription_expires_at.slice(0, 10)
      : ''
  )
  const [tokensLimit, setTokensLimit] = useState(user.tokens_limit ?? 0)
  const [role, setRole] = useState(user.role)
  const [verified, setVerified] = useState(user.email_verified)

  const updateUser = useMutation({
    mutationFn: () => adminService.updateUser(user.id, { role, email_verified: verified }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] })
      toast.success('User updated')
    },
    onError: () => toast.error('Failed to update user'),
  })

  const updateSub = useMutation({
    mutationFn: () =>
      adminService.updateSubscription(user.id, {
        plan,
        status: subStatus,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] })
      queryClient.invalidateQueries({ queryKey: ['adminStats'] })
      toast.success('Subscription updated')
    },
    onError: () => toast.error('Failed to update subscription'),
  })

  const handleSave = async () => {
    await updateUser.mutateAsync()
    await updateSub.mutateAsync()
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg space-y-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold">Edit: {user.email}</h3>

        <div className="grid grid-cols-2 gap-4">
          {/* Role */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="input-field">
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </div>

          {/* Email verified */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Email Verified</label>
            <select
              value={verified ? 'yes' : 'no'}
              onChange={(e) => setVerified(e.target.value === 'yes')}
              className="input-field"
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          {/* Plan */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Plan</label>
            <select value={plan} onChange={(e) => setPlan(e.target.value)} className="input-field">
              <option value="trial">trial</option>
              <option value="startup">startup</option>
              <option value="scale">scale</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Sub Status</label>
            <select value={subStatus} onChange={(e) => setSubStatus(e.target.value)} className="input-field">
              <option value="active">active</option>
              <option value="expired">expired</option>
              <option value="cancelled">cancelled</option>
            </select>
          </div>

          {/* Expires */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Expires At</label>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="input-field"
            />
          </div>

          {/* Tokens limit */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Tokens Limit</label>
            <input
              type="number"
              value={tokensLimit}
              onChange={(e) => setTokensLimit(Number(e.target.value))}
              className="input-field"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button
            onClick={handleSave}
            disabled={updateUser.isPending || updateSub.isPending}
            className="btn-primary text-sm"
          >
            {updateUser.isPending || updateSub.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
